<template>
  <Modal
    :model-value="modelValue"
    :content-class="'!max-w-2xl w-full'"
    :show-close="true"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <div class="flex flex-col gap-6">
      <div>
        <h2 class="text-xl font-semibold">
          {{ isReadOnly && isEdit ? t('viewTitle') : isEdit ? t('editTitle') : t('addTitle') }}
        </h2>
      </div>

      <!-- Media -->
      <div>
        <span class="mb-2 block text-sm font-medium text-obsidian-700">{{ t('media') }}</span>
        <div
          v-if="selectedMedia"
          class="flex items-center gap-2 rounded-lg border border-obsidian-200 p-2"
        >
          <div class="size-8 shrink-0 overflow-hidden rounded-md bg-obsidian-700">
            <img
              v-if="selectedMedia.posterFileUrl"
              :src="selectedMedia.posterFileUrl"
              class="h-full w-full object-cover"
              alt=""
            />
            <div v-else class="flex h-full w-full items-center justify-center text-white">
              <VideoIcon class="size-4" />
            </div>
          </div>
          <div class="flex min-w-0 flex-1 items-center gap-2">
            <p class="truncate text-sm font-medium">{{ selectedMedia.title }}</p>
            <MediaEncodingStatusBadge :status="selectedMedia.encodingStatus" size="sm" />
          </div>
          <CVButton v-if="!isReadOnly" variant="secondary" size="sm" @click="showPicker = true">
            {{ t('select') }}
          </CVButton>
          <CVButton variant="secondary" size="sm" @click="showPreviewModal = true">
            <PlayIcon class="size-4" /> {{ t('preview') }}
          </CVButton>
        </div>

        <div
          v-else
          class="flex items-center justify-between gap-2 rounded-lg border border-dashed border-obsidian-300 p-2"
        >
          <span class="text-sm text-obsidian-500">{{ t('chooseMedia') }}</span>
          <CVButton v-if="!isReadOnly" variant="secondary" size="sm" @click="showPicker = true">
            {{ t('select') }}
          </CVButton>
        </div>
      </div>

      <!-- Global -->
      <div class="rounded-md border border-obsidian-100 p-4">
        <div class="flex items-center justify-between gap-4">
          <div class="flex items-center gap-2">
            <GlobeIcon class="size-4 text-obsidian-400" />
            <span class="font-medium">{{ t('globalRotation') }}</span>
          </div>
          <Checkbox v-model="globalIncluded" :label="t('include')" :disabled="isReadOnly" />
        </div>
        <div v-if="globalIncluded" class="mt-3 flex items-center gap-3 pl-6">
          <span class="text-sm text-obsidian-500">{{ t('weight') }}</span>
          <Input
            :model-value="globalWeight"
            type="number"
            :min="0"
            size="sm"
            :disabled="isReadOnly"
            @update:model-value="globalWeight = sanitizeWeight($event)"
          />
          <span class="text-sm text-obsidian-400">{{
            globalWeight <= 0 ? t('pausedHint') : t('globalWeightHint')
          }}</span>
        </div>
      </div>

      <!-- Station overrides -->
      <div>
        <div class="mb-2 flex items-baseline justify-between">
          <span class="flex items-center gap-2 font-medium">
            <MapPinIcon class="size-4 text-obsidian-400" /> {{ t('stationOverrides') }}
          </span>
          <span class="text-xs text-obsidian-400">{{ t('overrideHint') }}</span>
        </div>

        <div
          v-if="stationRows.length"
          class="max-h-64 divide-y divide-obsidian-100 overflow-y-auto rounded-md border border-obsidian-100"
        >
          <div
            v-for="row in stationRows"
            :key="row.stationId"
            class="flex items-center justify-between gap-3 px-3 py-2"
          >
            <span class="min-w-0 flex-1 truncate text-sm">{{ row.label }}</span>

            <div v-if="row.override" class="flex items-center gap-2">
              <Input
                :model-value="row.weight"
                type="number"
                :min="0"
                size="sm"
                :disabled="isReadOnly"
                @update:model-value="setOverrideWeight(row.stationId, $event)"
              />
              <button
                v-if="!isReadOnly"
                type="button"
                class="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-obsidian-500 transition hover:bg-obsidian-50 hover:text-obsidian-800"
                @click="clearOverride(row.stationId)"
              >
                <RotateCcwIcon class="size-3.5" /> {{ t('default') }}
              </button>
            </div>

            <template v-else>
              <span v-if="!globalIncluded" class="text-xs text-obsidian-400">{{
                t('notPlaying')
              }}</span>
              <button
                v-if="!isReadOnly"
                type="button"
                class="flex items-center gap-1 text-xs"
                @click="addOverride(row.stationId)"
              >
                <span class="text-hazel-600 hover:underline">{{ t('override') }}</span>
              </button>
            </template>
          </div>
        </div>

        <p
          v-else
          class="rounded-lg border border-dashed border-obsidian-200 p-4 text-sm text-obsidian-400"
        >
          {{ t('noStations') }}
        </p>
      </div>

      <div class="flex items-center justify-between gap-3 border-t border-obsidian-100 pt-4">
        <CVButton v-if="isEdit && canDelete" variant="danger" @click="emit('remove')">
          <Trash2Icon class="size-4" /> {{ t('removeAll') }}
        </CVButton>
        <div class="ml-auto flex gap-3">
          <CVButton variant="secondary" @click="emit('update:modelValue', false)">
            {{ isReadOnly ? t('close') : t('cancel') }}
          </CVButton>
          <CVButton
            v-if="canSave"
            variant="default"
            :is-loading="isLoading"
            :disabled="!selectedMedia || !hasAnyPlacement"
            @click="handleSave"
          >
            {{ t('save') }}
          </CVButton>
        </div>
      </div>
    </div>

    <MediaPickerModal v-model="showPicker" @select="handlePickMedia" />

    <Modal v-model="showPreviewModal" :content-class="'!max-w-5xl w-full'" :show-close="true">
      <MediaPlayer v-if="showPreviewModal && selectedMedia" :media-id="selectedMedia.id" />
    </Modal>
  </Modal>
</template>

<script lang="ts" setup>
import Modal from '@/components/Modal.vue';
import Checkbox from '@/components/Checkbox.vue';
import Input from '@/components/Input.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import MediaPickerModal from '@/components/media/MediaPickerModal.vue';
import MediaEncodingStatusBadge from '@/components/badges/MediaEncodingStatusBadge.vue';
import MediaPlayer from '@/views/media/MediaPlayer.vue';
import type { Media as RosMedia } from '@carvia/ros-client-types';
import type {
  RotationMediaMediaSummaryDto,
  RotationMediaResponseDto,
} from '@carvia/ros-client-types';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import { useStations } from '@/queries/use-stations';
import { useAbility } from '@casl/vue';
import {
  GlobeIcon,
  MapPinIcon,
  PlayIcon,
  RotateCcwIcon,
  Trash2Icon,
  VideoIcon,
} from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

export interface RotationConfigPayload {
  mediaId: string;
  global: { include: boolean; weight: number };
  overrides: Array<{ stationId: string; weight: number }>;
}

const props = defineProps<{
  modelValue: boolean;
  media?: RotationMediaMediaSummaryDto | null;
  entries?: RotationMediaResponseDto[];
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'save', value: RotationConfigPayload): void;
  (e: 'remove'): void;
}>();

const { t } = useI18n();
const { getLocalizedField } = useLocalizedFields();
const { can } = useAbility();

const isEdit = computed(() => !!props.entries?.length);

const canDelete = computed(() => can('delete', 'RotationMedia'));
const canSave = computed(() =>
  isEdit.value ? can('update', 'RotationMedia') : can('create', 'RotationMedia'),
);
const isReadOnly = computed(() => !canSave.value);

const { data: stationsData } = useStations(500);
const stations = useFlattenPaginatedData(stationsData);

const selectedMedia = ref<RotationMediaMediaSummaryDto | null>(null);
const globalIncluded = ref(true);
const globalWeight = ref(1);
const overrides = ref<Record<string, number>>({});
const showPicker = ref(false);
const showPreviewModal = ref(false);

const sanitizeWeight = (value: number | null | undefined) =>
  typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0;

const stationRows = computed(() =>
  stations.value.map((station) => ({
    stationId: station.id,
    label: getLocalizedField(station.info),
    override: station.id in overrides.value,
    weight: overrides.value[station.id] ?? 0,
  })),
);

const hasAnyPlacement = computed(
  () => globalIncluded.value || Object.keys(overrides.value).length > 0,
);

watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) return;
    const globalEntry = props.entries?.find((entry) => !entry.station);
    const stationEntries = props.entries?.filter((entry) => entry.station) ?? [];

    selectedMedia.value = props.media ?? props.entries?.[0]?.media ?? null;
    globalIncluded.value = props.entries?.length ? !!globalEntry : true;
    globalWeight.value = globalEntry?.weight ?? 1;
    overrides.value = Object.fromEntries(
      stationEntries.map((entry) => [entry.station!.id, entry.weight]),
    );
  },
  { immediate: true },
);

function addOverride(stationId: string) {
  if (isReadOnly.value) return;
  overrides.value = {
    ...overrides.value,
    [stationId]: globalIncluded.value ? globalWeight.value : 1,
  };
}

function clearOverride(stationId: string) {
  if (isReadOnly.value) return;
  const next = { ...overrides.value };
  delete next[stationId];
  overrides.value = next;
}

function setOverrideWeight(stationId: string, value: number | null | undefined) {
  if (isReadOnly.value) return;
  overrides.value = { ...overrides.value, [stationId]: sanitizeWeight(value) };
}

function handlePickMedia(media: RosMedia) {
  selectedMedia.value = {
    id: media.id,
    title: media.title,
    encodingStatus: media.encodingStatus,
    posterFileUrl: media.posterFileUrl ?? null,
  };
}

async function handleSave() {
  if (isReadOnly.value || !selectedMedia.value || !hasAnyPlacement.value) return;
  emit('save', {
    mediaId: selectedMedia.value.id,
    global: { include: globalIncluded.value, weight: sanitizeWeight(globalWeight.value) },
    overrides: Object.entries(overrides.value).map(([stationId, weight]) => ({
      stationId,
      weight: sanitizeWeight(weight),
    })),
  });
}
</script>

<i18n lang="json">
{
  "en": {
    "addTitle": "Add video to rotation",
    "editTitle": "Configure rotation",
    "viewTitle": "View rotation",
    "media": "Media",
    "chooseMedia": "Choose media",
    "select": "Select",
    "preview": "Preview",
    "globalRotation": "Global rotation",
    "include": "Play at all stations",
    "weight": "Weight",
    "globalWeightHint": "Higher weight plays more often.",
    "pausedHint": "Paused — kept but not played.",
    "stationOverrides": "Station overrides",
    "overrideHint": "Override the global weight per station.",
    "default": "Default",
    "notPlaying": "Not playing",
    "override": "Override",
    "noStations": "No stations available.",
    "removeAll": "Remove from rotation",
    "cancel": "Cancel",
    "close": "Close",
    "save": "Save"
  },
  "de": {
    "addTitle": "Video zur Rotation hinzufügen",
    "editTitle": "Rotation konfigurieren",
    "viewTitle": "Rotation ansehen",
    "media": "Medium",
    "chooseMedia": "Medium auswählen",
    "select": "Auswählen",
    "preview": "Vorschau",
    "globalRotation": "Globale Rotation",
    "include": "An allen Stationen abspielen",
    "weight": "Gewichtung",
    "globalWeightHint": "Höhere Gewichtung wird häufiger abgespielt.",
    "pausedHint": "Pausiert — bleibt erhalten, wird aber nicht abgespielt.",
    "stationOverrides": "Stationsüberschreibungen",
    "overrideHint": "Globale Gewichtung pro Station überschreiben.",
    "default": "Standard",
    "notPlaying": "Wird nicht abgespielt",
    "override": "Überschreiben",
    "noStations": "Keine Stationen verfügbar.",
    "removeAll": "Aus Rotation entfernen",
    "cancel": "Abbrechen",
    "close": "Schließen",
    "save": "Speichern"
  },
  "es": {
    "addTitle": "Añadir vídeo a la rotación",
    "editTitle": "Configurar rotación",
    "viewTitle": "Ver rotación",
    "media": "Medio",
    "chooseMedia": "Seleccionar medio",
    "select": "Seleccionar",
    "preview": "Vista previa",
    "globalRotation": "Rotación global",
    "include": "Reproducir en todas las estaciones",
    "weight": "Peso",
    "globalWeightHint": "Un peso mayor se reproduce más a menudo.",
    "pausedHint": "En pausa — se mantiene pero no se reproduce.",
    "stationOverrides": "Anulaciones por estación",
    "overrideHint": "Anula el peso global por estación.",
    "default": "Predeterminado",
    "notPlaying": "No se reproduce",
    "override": "Anular",
    "noStations": "No hay estaciones disponibles.",
    "removeAll": "Quitar de la rotación",
    "cancel": "Cancelar",
    "close": "Cerrar",
    "save": "Guardar"
  }
}
</i18n>
